import React from 'react';
import { Box, Button, Divider, Drawer, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TemplateIcon from '@mui/icons-material/Description';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ImageIcon from '@mui/icons-material/Image';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ListIcon from '@mui/icons-material/List';
import SettingsIcon from '@mui/icons-material/Settings';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import StyleIcon from '@mui/icons-material/Style';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CategoryFilter from '../components/CategoryFilter';
import { ICategory, IUser } from '../types/damTypes';
import { hasPermission, canAccessProductComparison } from '../utils/permissionUtils';

interface TabletCategoryDrawerProps {
  open: boolean;
  onClose: () => void;
  categories: ICategory[];
  selectedCategoryIds: string[];
  onCategorySelect: (categoryIds: string[]) => void;
  currentUser?: IUser | null;
  onOpenTrends?: () => void;
  onOpenBriefs?: () => void;
  onOpenBrandGuide?: () => void;
  onOpenPptPolicies?: () => void;
  onOpenPresentations?: () => void;
  onOpenPrompts?: () => void;
  onOpenAiImageStudio?: () => void;
  onOpenNyhetslanseringar?: () => void;
  onOpenBarcodeGenerator?: () => void;
  onOpenIssuesTasks?: () => void;
  onOpenSettings?: () => void;
}

export default function TabletCategoryDrawer({
  open,
  onClose,
  categories,
  selectedCategoryIds,
  onCategorySelect,
  currentUser = null,
  onOpenTrends,
  onOpenBriefs,
  onOpenBrandGuide,
  onOpenPptPolicies,
  onOpenPresentations,
  onOpenPrompts,
  onOpenAiImageStudio,
  onOpenNyhetslanseringar,
  onOpenBarcodeGenerator,
  onOpenIssuesTasks,
  onOpenSettings,
}: TabletCategoryDrawerProps) {
  const canAccessIssuesTasks = hasPermission(currentUser, 'issues:read') || hasPermission(currentUser, 'tasks:read');
  const canOpenSettings =
    hasPermission(currentUser, 'categories:read') ||
    hasPermission(currentUser, 'categories:create') ||
    hasPermission(currentUser, 'templates:read') ||
    hasPermission(currentUser, 'downloadTemplates:read') ||
    hasPermission(currentUser, 'assets:update') ||
    hasPermission(currentUser, 'system:manageSettings') ||
    canAccessIssuesTasks ||
    canAccessProductComparison(currentUser) ||
    hasPermission(currentUser, 'roles:read') ||
    hasPermission(currentUser, 'users:read');

  type NavigationItem = {
    id: string;
    label: string;
    icon: React.ReactNode;
    action: () => void;
  };

  const navigationItems: NavigationItem[] = [];
  if (onOpenTrends) {
    navigationItems.push({ id: 'trends', label: 'Trender', icon: <TrendingUpIcon fontSize="small" />, action: onOpenTrends });
  }
  if (onOpenBriefs) {
    navigationItems.push({ id: 'briefs', label: 'Skapa brief', icon: <TemplateIcon fontSize="small" />, action: onOpenBriefs });
  }
  if (onOpenBrandGuide) {
    navigationItems.push({ id: 'brand-guide', label: 'Grafisk manual', icon: <StyleIcon fontSize="small" />, action: onOpenBrandGuide });
  }
  if (onOpenPptPolicies) {
    navigationItems.push({ id: 'ppt-policies', label: 'PPT Policys', icon: <TemplateIcon fontSize="small" />, action: onOpenPptPolicies });
  }
  if (onOpenPresentations) {
    navigationItems.push({ id: 'presentations', label: 'Presentations', icon: <SlideshowIcon fontSize="small" />, action: onOpenPresentations });
  }
  if (onOpenPrompts) {
    navigationItems.push({ id: 'prompts', label: 'Prompt', icon: <AutoAwesomeIcon fontSize="small" />, action: onOpenPrompts });
  }
  if (onOpenAiImageStudio) {
    navigationItems.push({ id: 'ai-image-studio', label: 'Athena AI', icon: <ImageIcon fontSize="small" />, action: onOpenAiImageStudio });
  }
  if (onOpenNyhetslanseringar) {
    navigationItems.push({ id: 'nyhetslanseringar', label: 'Nyhetslanseringar', icon: <RocketLaunchIcon fontSize="small" />, action: onOpenNyhetslanseringar });
  }
  if (onOpenBarcodeGenerator) {
    navigationItems.push({ id: 'barcode', label: 'EAN-koder', icon: <StorefrontIcon fontSize="small" />, action: onOpenBarcodeGenerator });
  }
  if (canAccessIssuesTasks && onOpenIssuesTasks) {
    navigationItems.push({ id: 'issues', label: 'Buggar & Roadmap', icon: <ListIcon fontSize="small" />, action: onOpenIssuesTasks });
  }
  if (canOpenSettings && onOpenSettings) {
    navigationItems.push({ id: 'settings', label: 'Inställningar', icon: <SettingsIcon fontSize="small" />, action: onOpenSettings });
  }

  const handleNavigationClick = (action: () => void) => {
    action();
    onClose();
  };

  return (
    <Drawer anchor="left" open={open} onClose={onClose}>
      <Box sx={{ width: 360, maxWidth: '88vw', height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Meny
          </Typography>
          <IconButton onClick={onClose} aria-label="Stäng meny">
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        <Box sx={{ p: 2 }}>
          <Typography variant="overline" sx={{ mb: 1, display: 'block', color: 'text.secondary' }}>
            Kategorier
          </Typography>
          <Button
            fullWidth
            variant={selectedCategoryIds.length === 0 ? 'contained' : 'outlined'}
            onClick={() => {
              onCategorySelect([]);
              onClose();
            }}
          >
            Alla
          </Button>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', px: 2, pb: 2 }}>
          <CategoryFilter
            categories={categories}
            selectedCategoryIds={selectedCategoryIds}
            onCategoryToggle={() => {}}
            onCategorySelect={(ids) => {
              onCategorySelect(ids);
              onClose();
            }}
            align="start"
          />
        </Box>

        {navigationItems.length > 0 && (
          <>
            <Divider />
            <Box sx={{ px: 1.5, py: 1.5 }}>
              <Typography variant="overline" sx={{ px: 1, color: 'text.secondary' }}>
                Sidor
              </Typography>
              <List dense sx={{ mt: 0.5 }}>
                {navigationItems.map((item) => (
                  <ListItemButton
                    key={item.id}
                    onClick={() => handleNavigationClick(item.action)}
                    sx={{ borderRadius: 1, mb: 0.5 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                ))}
              </List>
            </Box>
          </>
        )}
      </Box>
    </Drawer>
  );
}
